import React, { useState } from 'react';
import {
  BiBold,
  BiItalic,
  BiListOl,
  BiListUl,
  BiStrikethrough,
  BiLink,
  BiUnlink,
  BiImageAdd,
} from 'react-icons/bi';
import { Editor } from '@tiptap/react';
import { Modal, Select, Upload } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { PortableTextInputToolbarButton } from './PortableTextInputToolbarButton';
import { ImageDrawAndUploadModal } from '../ImageDrawAndUploadModal';

type Props = {
  editor: Editor | null
}

export function PortableTextInputToolbar({ editor }: Props) {
  const { t } = useTranslation('components/input/portable-text-input')
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const handleToggleBold = () => editor?.chain().focus().toggleBold().run()
  const handleToggleItalic = () => editor?.chain().focus().toggleItalic().run()
  const handleToggleStrike = () => editor?.chain().focus().toggleStrike().run()
  const handleToggleBulletList = () => editor?.chain().focus().toggleBulletList().run()
  const handleToggleOrderedList = () => editor?.chain().focus().toggleOrderedList().run()
  const handleInsertOrRemoveLink = () => {
    if (editor?.isActive('link')) {
      editor.chain().focus().unsetLink().run()
    } else {
      const previousUrl = editor?.getAttributes('link').href
      const url = window.prompt('URL', previousUrl)
      if (url) {
        editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      }
    }
  }
  const handleSelectStyle = (value: string) => {
    if (value === 'h1') {
      editor?.chain().focus().toggleHeading({ level: 1 }).run()
    } else if (value === 'h2') {
      editor?.chain().focus().toggleHeading({ level: 2 }).run()
    } else if (value === 'p') {
      editor?.chain().focus().setParagraph().run()
    }
  }
  const handleOpenImageModal = () => {
    setImageModalOpen(true)
  }
  const handleInsertImage = (url: string) => {
    editor?.commands.setImage({ src: url })
  }

  return (
    <>
      <div className="w-full">
        <div className="flex flex-wrap items-center justify-start">
          <div className="w-[15rem] flex flex-col m-1">
            <Select
              value={editor?.isActive('heading', { level: 2 }) ? 'h2' : 'p'}
              options={[
                { label: t('styles.heading-2'), value: 'h2' },
                { label: t('styles.paragraph'), value: 'p' },
              ]}
              onSelect={handleSelectStyle}
            />
          </div>
          <PortableTextInputToolbarButton
            icon={BiBold}
            isActive={editor?.isActive('bold') ?? false}
            onClick={handleToggleBold}
          />
          <PortableTextInputToolbarButton
            icon={BiItalic}
            isActive={editor?.isActive('italic') ?? false}
            onClick={handleToggleItalic}
          />
          <PortableTextInputToolbarButton
            icon={BiStrikethrough}
            isActive={editor?.isActive('strike') ?? false}
            onClick={handleToggleStrike}
          />
          <PortableTextInputToolbarButton
            icon={BiListUl}
            isActive={editor?.isActive('bulletList') ?? false}
            onClick={handleToggleBulletList}
          />
          <PortableTextInputToolbarButton
            icon={BiListOl}
            isActive={editor?.isActive('orderedList') ?? false}
            onClick={handleToggleOrderedList}
          />
          <PortableTextInputToolbarButton
            icon={editor?.isActive('link') ? BiUnlink : BiLink}
            isActive={editor?.isActive('link') ?? false}
            onClick={handleInsertOrRemoveLink}
          />
          <PortableTextInputToolbarButton
            icon={BiImageAdd}
            onClick={handleOpenImageModal}
          />
        </div>
      </div>
      <ImageDrawAndUploadModal
        open={imageModalOpen}
        onClose={() => {
          setImageModalOpen(false)
        }}
        onDone={handleInsertImage}
      />
    </>
  )
}